import { transparentize } from 'polished';
import React, { PropsWithChildren } from 'react';
import styled, { css } from 'styled-components';
import {
  chevronPadding,
  fullPageWhiteRectangleStyles,
} from '../../commonStyles/chevronStyles';
import colors from '../../helpers/colors';
import { ColumnsByBreakpoint } from '../../helpers/columns/types';
import { stylePerBreakpoint } from '../../helpers/mediaQuery';
import spacing from '../../helpers/spacing';
import { Breakpoint } from '../../theme';
import fluidGridColumns from '../../theme/fluidGridColumns';

type ChevronBackgroundProps = {
  reverse?: boolean;
};

const CHEVRON_WIDE_SIDE_ANGLE = -2.5; // degrees
const CHEVRON_SHARP_SIDE_ANGLE = 12.5; // degrees

const CHEVRON_WIDE_TRIANGLE_WIDTH_TO_HEIGHT_RATIO = 0.04366; // truncation of (tan 2.5deg)
const CHEVRON_NARROW_TRIANGLE_WIDTH_TO_HEIGHT_RATIO = 0.22169; // truncation of (tan 12.5deg)

const topChevronPointPosition: ColumnsByBreakpoint = {
  desktop: 11,
  tabletLandscape: 11,
  tabletPortrait: 7,
  mobile: 3,
};
const breakpoints = Object.keys(topChevronPointPosition) as Breakpoint[];
const bottomChevronPointPosition = Object.fromEntries(
  breakpoints.map((breakpoint) => [
    breakpoint,
    fluidGridColumns[breakpoint] - topChevronPointPosition[breakpoint],
  ])
) as ColumnsByBreakpoint;

const getAngle = (reverse: boolean | undefined, degrees: number): string =>
  !reverse ? `${degrees}deg` : `${-degrees}deg`;

const ChevronBottomBackground = styled.div<ChevronBackgroundProps>`
  background: ${({ theme }) => transparentize(0.95, colors.core01({ theme }))};
  margin: ${spacing(4)} calc(50% - 50vw);
  padding: 0 calc(50vw - 50%);

  display: flex;
  justify-content: center;
  position: relative;
  overflow: hidden;

  &::before {
    transform: rotate(${({ reverse }) => getAngle(reverse, CHEVRON_SHARP_SIDE_ANGLE)});
    transform-origin: top center;
    top: 100%;
    ${({ reverse }) => fullPageWhiteRectangleStyles(bottomChevronPointPosition, reverse)}
  }
  &::after {
    transform: rotate(${({ reverse }) => getAngle(reverse, CHEVRON_WIDE_SIDE_ANGLE)});
    transform-origin: top center;
    top: 100%;
    ${({ reverse }) => fullPageWhiteRectangleStyles(bottomChevronPointPosition, reverse)}
  }

  /* This padding covers the top and bottom triangles in the chevron.
    This allows us to easily enforce the spacing between the content and the triangle.
    
    Usually the spacing between the content and triangle is calculated from the corner of 
    the full triangle. But when screen width > 1440px, we calculate this spacing from the
    the subtriangle that has width 1440px - in this case, the height of the left-hand side
    of the triangle is larger.
   */
  ${stylePerBreakpoint(
    (breakpoint) => css`
      padding-bottom: ${chevronPadding(
        bottomChevronPointPosition[breakpoint],
        fluidGridColumns[breakpoint],
        CHEVRON_NARROW_TRIANGLE_WIDTH_TO_HEIGHT_RATIO,
        CHEVRON_WIDE_TRIANGLE_WIDTH_TO_HEIGHT_RATIO
      )};
      padding-top: ${chevronPadding(
        topChevronPointPosition[breakpoint],
        fluidGridColumns[breakpoint],
        CHEVRON_WIDE_TRIANGLE_WIDTH_TO_HEIGHT_RATIO,
        CHEVRON_NARROW_TRIANGLE_WIDTH_TO_HEIGHT_RATIO
      )};
    `
  )}

  @media (min-width: 1440px) {
    padding-top: calc(1440px * ${CHEVRON_WIDE_TRIANGLE_WIDTH_TO_HEIGHT_RATIO});
    padding-bottom: calc(1440px * ${CHEVRON_WIDE_TRIANGLE_WIDTH_TO_HEIGHT_RATIO});
  }
`;

const ChevronTopLeft = styled.div<ChevronBackgroundProps>`
  transform: rotate(${({ reverse }) => getAngle(reverse, CHEVRON_WIDE_SIDE_ANGLE)});
  transform-origin: bottom center;
  bottom: 100%;
  ${({ reverse }) => fullPageWhiteRectangleStyles(topChevronPointPosition, reverse)}
`;
const ChevronTopRight = styled.div<ChevronBackgroundProps>`
  transform: rotate(${({ reverse }) => getAngle(reverse, CHEVRON_SHARP_SIDE_ANGLE)});
  transform-origin: bottom center;
  bottom: 100%;
  ${({ reverse }) => fullPageWhiteRectangleStyles(topChevronPointPosition, reverse)}
`;

const DoubleChevronBackground: React.FC<PropsWithChildren<ChevronBackgroundProps>> = ({
  reverse,
  children,
}) => (
  <ChevronBottomBackground reverse={reverse}>
    <ChevronTopLeft reverse={reverse} />
    <ChevronTopRight reverse={reverse} />
    {children}
  </ChevronBottomBackground>
);

export default DoubleChevronBackground;
